import { useContext } from 'react';
import { useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import restrictions from '@gin-betting/restrictions';
import { createStructuredSelector } from 'reselect';

import type { State } from 'types';
import {
    useChangeStake,
    useExpressSystemErrors,
    usePlaceBet,
} from '../../../hooks';
import { RestrictionsContext } from '../../../restrictions';

const {
    selectors: { selectBetslipBetWithStatus, selectBetslipBetTotals },
    utils: { getNumberCombinations },
} = betslip;

const {
    selectors: { selectLowerMaxBetBetslipBet },
} = restrictions;

interface Input {
    betslipBetId: string;
}

type Output = {
    changeStakeHandler: (stake: string) => void;
    betslipOddsSize: number;
    stake: string;
    errorMsgs: Array<string | string[]>;
    finalTotalWin: number;
    finalTotalStake: number;
    maxStakeForInput: string;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
} | null;

const useSystemPart = ({ betslipBetId }: Input): Output => {
    const { errorMsgs } = useExpressSystemErrors({
        betslipBetId,
        isFreebetSelected: false,
    });
    const changeStakeHandler = useChangeStake({ betslipBetId });
    const { state: restrictionsState } = useContext(RestrictionsContext);

    const {
        totals,
        betslipBetWithStatus,
        maxBet,
    } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState })
    );

    if (!betslipBetWithStatus) return null;

    const { betslipOddIds, stake, systemSize } = betslipBetWithStatus;

    const maxStake = !maxBet ? '' : maxBet;
    const { totalWin } = totals;

    const betslipOddsSize = betslipOddIds.count();

    const finalTotalStake = +stake;

    const { onKeyDown } = usePlaceBet({
        totalStake: finalTotalStake,
        betslipBetId,
    });

    const numberCombinations = getNumberCombinations(systemSize, betslipOddIds);

    const maxStakeForInput = maxStake && numberCombinations ? maxStake : '';

    const finalTotalWin = numberCombinations
        ? totalWin / numberCombinations
        : totalWin;

    return {
        changeStakeHandler,
        betslipOddsSize,
        stake,
        errorMsgs,
        finalTotalStake,
        finalTotalWin,
        maxStakeForInput,
        onKeyDown,
    };
};

const selectProps = createStructuredSelector({
    betslipBetWithStatus: selectBetslipBetWithStatus,
    totals: selectBetslipBetTotals,
    maxBet: selectLowerMaxBetBetslipBet,
});

export default useSystemPart;
